import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { GIFT_RESOURCE_ICONS } from '../../constants/giftResourceIcons'
import type { RootState } from '../../store'
import styles from './ResourceClaimFlyLayer.module.scss'

/**
 * Resource-icon claim burst — each particle is the claimed resource's own icon
 * (iron icon for iron, wood icon for wood, …) instead of a coin/gem.
 *
 * Flow: a building modal's claim succeeds → its host's `triggerClaimEffect`
 * calls `flyResourceClaim(type)` → particles launch from the player's last
 * click (the Claim button) and arc up to the matching top-bar resource counter,
 * which then pops. The counter is located by the `data-resource-balance="<key>"`
 * attribute on the TopMenuBar rows; if it is not on screen the effect no-ops.
 */

export type ClaimResourceType = 'ECS' | 'WHEAT' | 'BREAD' | 'WOOD' | 'STONE' | 'IRON' | 'WATER'

type Rect = { left: number; top: number; width: number; height: number }

type ResourceFlyParticle = {
  id: string
  iconUrl: string
  sx: number
  sy: number
  mx: number
  my: number
  ex: number
  ey: number
  delay: number
  duration: number
  size: number
}

type FlyBatch = { id: string; particles: ResourceFlyParticle[] }

const FLY_DURATION_MS = 760
/** Treat the last click as the launch origin only if it is this recent. */
const POINTER_FRESH_MS = 6000

// ── last-pointer tracking (launch origin for the burst) ─────────────────────
let lastPointer = { x: 0, y: 0, t: -Infinity }
let pointerTracking = false
function ensurePointerTracking() {
  if (pointerTracking || typeof window === 'undefined') return
  pointerTracking = true
  window.addEventListener(
    'pointerdown',
    (e) => {
      lastPointer = { x: e.clientX, y: e.clientY, t: Date.now() }
    },
    { capture: true, passive: true },
  )
}
ensurePointerTracking()

// ── pub/sub so the imperative flyResourceClaim() reaches the mounted layer ───
type Listener = (batch: FlyBatch) => void
const listeners = new Set<Listener>()
function emit(batch: FlyBatch) {
  listeners.forEach((fn) => fn(batch))
}

function findTargetEl(key: string): HTMLElement | null {
  if (typeof document === 'undefined') return null
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(`[data-resource-balance="${key}"]`))
  // A hidden responsive copy reports a zero-size rect — prefer the visible one.
  const visible = nodes.find((el) => {
    const r = el.getBoundingClientRect()
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight
  })
  return visible ?? nodes[0] ?? null
}

function resolveSource(target: Rect): Rect {
  const fresh = Date.now() - lastPointer.t < POINTER_FRESH_MS
  if (fresh) return { left: lastPointer.x, top: lastPointer.y, width: 0, height: 0 }
  // No recent click (auto-claim / keyboard): rise up from below the counter.
  const cx = target.left + target.width / 2
  return { left: cx, top: target.top + 220, width: 0, height: 0 }
}

function buildParticles(from: Rect, to: Rect, iconUrl: string, count: number, seed: string): ResourceFlyParticle[] {
  const sx = from.left + from.width / 2
  const sy = from.top + from.height / 2
  const ex = to.left + to.width / 2
  const ey = to.top + to.height / 2
  const n = Math.max(7, Math.min(count, 16))

  return Array.from({ length: n }, (_, i) => {
    const t = i / Math.max(n - 1, 1)
    const spread = 0.24
    const arcLift = -64 - (i % 3) * 20
    const mx = sx + (ex - sx) * (0.4 + t * 0.16) + (i % 2 === 0 ? -1 : 1) * spread * Math.abs(ex - sx) * 0.3
    const my = sy + (ey - sy) * 0.4 + arcLift
    const jitterX = (Math.random() - 0.5) * 30
    const jitterY = (Math.random() - 0.5) * 18

    return {
      id: `rcf-${seed}-${i}`,
      iconUrl,
      sx: sx + jitterX - (i % 3) * 4,
      sy: sy + jitterY,
      mx: mx + jitterX * 0.5,
      my: my + jitterY * 0.4,
      ex: ex + (Math.random() - 0.5) * 14,
      ey: ey + (Math.random() - 0.5) * 10,
      delay: i * 40,
      duration: FLY_DURATION_MS,
      size: 18 + (i % 4) * 4,
    }
  })
}

function maxMs(particles: ResourceFlyParticle[]): number {
  return particles.reduce((m, p) => Math.max(m, p.delay + p.duration), 0)
}

function popTarget(el: HTMLElement, delay: number) {
  if (typeof el.animate !== 'function') return
  window.setTimeout(() => {
    try {
      el.animate(
        [
          { transform: 'scale(1)', filter: 'brightness(1)' },
          { transform: 'scale(1.18)', filter: 'brightness(1.45)' },
          { transform: 'scale(1)', filter: 'brightness(1)' },
        ],
        { duration: 460, easing: 'cubic-bezier(0.34, 1.4, 0.64, 1)' },
      )
    } catch {
      /* decorative — ignore */
    }
  }, Math.max(0, delay))
}

// ── press-fire bookkeeping so the gain-watcher never double-fires ────────────
/** When an explicit (press) fly last fired, per resource key. */
const lastFlyAt: Record<string, number> = {}
/** A gain within this window of a press-fire is assumed to be that same claim's API landing. */
const AUTO_DEDUP_MS = 15000
/** Auto-fire on a balance gain only if a click happened this recently (i.e. a real claim press). */
const AUTO_POINTER_FRESH_MS = 6000

let flyCounter = 0

type FlySource = HTMLElement | DOMRect | { x: number; y: number }

function rectFromSource(source: FlySource | null | undefined): Rect | null {
  if (!source) return null
  if (typeof HTMLElement !== 'undefined' && source instanceof HTMLElement) {
    const r = source.getBoundingClientRect()
    return { left: r.left, top: r.top, width: r.width, height: r.height }
  }
  if ('width' in source && 'height' in source) {
    return { left: source.left, top: source.top, width: source.width, height: source.height }
  }
  return { left: source.x, top: source.y, width: 0, height: 0 }
}

/** lowercase reward keys (as they appear in result payloads) → fly type. */
const RESOURCE_KEY_TO_TYPE: Record<string, ClaimResourceType> = {
  water: 'WATER',
  wheat: 'WHEAT',
  bread: 'BREAD',
  wood: 'WOOD',
  stone: 'STONE',
  iron: 'IRON',
  ecs: 'ECS',
  ecstokenamount: 'ECS',
}

/**
 * Icons for the claim burst. Defaults to the shared reward-icon map, overriding ECS with the
 * gem artwork: what the player mines and claims at the tower is gems, so the burst should show
 * the same gem the shop and HUD use — not the legacy ECS coin. Scoped to this layer on purpose;
 * `GIFT_RESOURCE_ICONS.ecs` still drives quest/alliance/PvP reward lists elsewhere.
 */
const CLAIM_ICONS: Record<string, string> = {
  ...GIFT_RESOURCE_ICONS,
  ecs: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/eclipsera-gem.webp',
}

function flyInternal(resourceType: ClaimResourceType, fromOverride: Rect | null): void {
  if (typeof document === 'undefined') return
  const key = resourceType.toLowerCase()
  lastFlyAt[key] = Date.now()
  const iconUrl = CLAIM_ICONS[key]
  if (!iconUrl) return

  const targetEl = findTargetEl(key)
  if (!targetEl) return

  const to = targetEl.getBoundingClientRect()
  const from = fromOverride ?? resolveSource(to)

  flyCounter += 1
  const seed = `${flyCounter}-${Date.now()}`
  const particles = buildParticles(from, to, iconUrl, 11, seed)
  if (particles.length === 0) return

  emit({ id: `batch-${seed}`, particles })
  popTarget(targetEl, maxMs(particles) - 120)
}

/**
 * Fire a resource-icon particle burst from the player's last click (the Claim
 * button) to the matching resource counter. Safe to call from anywhere; no-ops
 * when the counter is not on screen.
 */
export function flyResourceClaim(resourceType: ClaimResourceType): void {
  flyInternal(resourceType, null)
}

const ENERGY_ICON = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mvm/energy/energy-icon.webp'

/**
 * Energy-claim burst — energy icons fly from the player's last click (the Buy button)
 * up INTO the energy counter, which then pops. Same "claim" effect as resources, used
 * after buying an energy refill (works on world map, diplomacy and espionage pages).
 */
export function flyEnergyClaim(): void {
  if (typeof document === 'undefined') return
  lastFlyAt['energy'] = Date.now()
  const targetEl = findTargetEl('energy')
  if (!targetEl) return

  const to = targetEl.getBoundingClientRect()
  const from = resolveSource(to)

  flyCounter += 1
  const seed = `energy-${flyCounter}-${Date.now()}`
  const particles = buildParticles(
    from,
    { left: to.left, top: to.top, width: to.width, height: to.height },
    ENERGY_ICON,
    11,
    seed,
  )
  if (particles.length === 0) return

  emit({ id: `batch-${seed}`, particles })
  popTarget(targetEl, maxMs(particles) - 120)
}

/**
 * Like flyResourceClaim, but launches from an explicit element/rect/point
 * instead of the last click — for ASYNC gains (espionage steal report, diplomacy
 * gift, world-map battle loot) that arrive with no recent pointer.
 */
export function flyResourceClaimFrom(
  source: FlySource | null | undefined,
  resourceType: ClaimResourceType,
): void {
  flyInternal(resourceType, rectFromSource(source))
}

/**
 * Fire a burst for every resource gained (amount > 0) in a reward payload like
 * { bread: 10, wood: 5 }, all launched from `source`.
 */
export function flyResourceGains(
  source: FlySource | null | undefined,
  gains: Record<string, number | string | undefined | null> | null | undefined,
): void {
  if (!gains) return
  for (const [rawKey, amount] of Object.entries(gains)) {
    const type = RESOURCE_KEY_TO_TYPE[rawKey.toLowerCase()]
    if (type && Number(amount) > 0) flyResourceClaimFrom(source, type)
  }
}

/**
 * Fire a burst when a resource balance increases from ANY source (mission
 * reward, quest, gift, defense loot, …). Skipped when an explicit press-fire
 * already covered this resource (dedup) or when there was no recent click to
 * launch from — in that case the counter still counts up, just without a burst.
 */
export function autoFlyResourceClaim(resourceType: ClaimResourceType): void {
  const key = resourceType.toLowerCase()
  const now = Date.now()
  if (now - (lastFlyAt[key] || 0) < AUTO_DEDUP_MS) return
  if (now - lastPointer.t > AUTO_POINTER_FRESH_MS) return
  flyResourceClaim(resourceType)
}

/** Redux user resource fields → fly type. */
const WATCHED_RESOURCE_FIELDS: Array<{ field: string; type: ClaimResourceType }> = [
  { field: 'water', type: 'WATER' },
  { field: 'wheat', type: 'WHEAT' },
  { field: 'bread', type: 'BREAD' },
  { field: 'wood', type: 'WOOD' },
  { field: 'stone', type: 'STONE' },
  { field: 'iron', type: 'IRON' },
  { field: 'ecsTokenAmount', type: 'ECS' },
]

/**
 * Mounted once, globally. Watches the player's Redux resource balances and fires
 * an auto-burst whenever one increases, so every reward path (daily missions,
 * quests, gifts, …) shows the fly effect with no per-site wiring.
 */
export const ResourceGainWatcher: React.FC = () => {
  const signature = useSelector((state: RootState) => {
    const u = state.userModule?.user as Record<string, unknown> | undefined
    return WATCHED_RESOURCE_FIELDS.map((r) => Number(u?.[r.field] ?? 0)).join('|')
  })
  const prevRef = useRef<number[] | null>(null)

  useEffect(() => {
    const curr = signature.split('|').map(Number)
    const prev = prevRef.current
    if (prev) {
      WATCHED_RESOURCE_FIELDS.forEach((r, i) => {
        if (curr[i] > (prev[i] ?? 0) + 1e-6) autoFlyResourceClaim(r.type)
      })
    }
    prevRef.current = curr
  }, [signature])

  return null
}

const ResourceClaimFlyLayer: React.FC = () => {
  const [batches, setBatches] = useState<FlyBatch[]>([])

  useEffect(() => {
    ensurePointerTracking()
    const listener: Listener = (batch) => {
      setBatches((prev) => [...prev, batch])
      const ttl = maxMs(batch.particles) + 160
      window.setTimeout(() => {
        setBatches((prev) => prev.filter((b) => b.id !== batch.id))
      }, ttl)
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  if (typeof document === 'undefined' || batches.length === 0) return null

  const particles = batches.flatMap((b) => b.particles)

  return createPortal(
    <div className={styles.layer} aria-hidden>
      {particles.map((p) => (
        <img
          key={p.id}
          src={p.iconUrl}
          alt=""
          draggable={false}
          className={styles.particle}
          style={
            {
              width: p.size,
              height: p.size,
              ['--rc-sx']: `${p.sx - p.size / 2}px`,
              ['--rc-sy']: `${p.sy - p.size / 2}px`,
              ['--rc-mx']: `${p.mx - p.size / 2}px`,
              ['--rc-my']: `${p.my - p.size / 2}px`,
              ['--rc-ex']: `${p.ex - p.size / 2}px`,
              ['--rc-ey']: `${p.ey - p.size / 2}px`,
              ['--rc-dur']: `${p.duration}ms`,
              ['--rc-delay']: `${p.delay}ms`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>,
    document.body,
  )
}

export default ResourceClaimFlyLayer
